import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import vishwasabt from "../assests/vishwas-abt.png";

const About = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-50px' });

  const fadeUp = {
    hidden: { opacity: 0, y: 20 },
    visible: (i = 0) => ({
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, delay: i * 0.1, ease: [0.25, 1, 0.5, 1] },
    }),
  };

  const fadeRight = {
    hidden: { opacity: 0, x: -30 },
    visible: (i = 0) => ({
      opacity: 1,
      x: 0,
      transition: { duration: 0.6, delay: i * 0.1, ease: [0.25, 1, 0.5, 1] },
    }),
  };

  const achievements = [
    {
      title: 'DIRT TRACK CHAMPION',
      subtitle: 'Indian National',
      year: '2003 / 04',
    },
    {
      title: 'CROSS COUNTRY CHAMPION',
      subtitle: 'Indian National',
      year: '2015',
    },
    {
      title: 'CROSS COUNTRY CHAMPION',
      subtitle: 'Indian National',
      year: '2016',
    },
  ];

  const personalDetails = [
    { label: 'Bike', value: 'YAMAHA WR 250 CC' },
    { label: 'Height', value: `5'10"` },
    { label: 'Weight', value: '70 Kgs' },
  ];

  return (
    <section
      id="about"
      className="relative py-16 md:py-24 overflow-hidden bg-darker"
    >
      {/* Background gradient */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute inset-0 bg-gradient-to-br from-black via-[#140404] to-black"></div>
        <div className="absolute top-0 right-0 w-[350px] md:w-[500px] h-[350px] md:h-[500px] rounded-full opacity-20 blur-3xl bg-primary"></div>
        <div className="absolute bottom-0 left-0 w-[250px] md:w-[400px] h-[250px] md:h-[400px] rounded-full opacity-10 blur-3xl bg-white"></div>
      </div>

      {/* Checkered pattern top-right corner */}
      <div className="absolute top-6 right-6 grid grid-cols-6 gap-[2px] opacity-15 pointer-events-none">
        {[...Array(24)].map((_, i) => (
          <div
            key={i}
            className={`w-2 h-2 ${
              (Math.floor(i / 6) + i) % 2 === 0 ? 'bg-white' : 'bg-transparent'
            }`}
          ></div>
        ))}
      </div>

      <div
        ref={ref}
        className="relative max-w-6xl mx-auto px-4 sm:px-6"
      >
        {/* Main Card Container */}
        <div className="relative bg-gradient-to-br from-[#0d0d0d] via-[#141414] to-[#0a0a0a] rounded-2xl md:rounded-3xl overflow-hidden border border-white/10 shadow-2xl">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-0">
            
            {/* LEFT SIDE - Content */}
            <div className="lg:col-span-7 p-6 sm:p-10 lg:p-12 xl:p-14 flex flex-col justify-center relative z-10 order-2 lg:order-1">
              
              {/* HELLO tag */}
              <motion.div
                variants={fadeRight}
                initial="hidden"
                animate={isInView ? 'visible' : 'hidden'}
                className="flex items-center gap-3 mb-4 sm:mb-5"
              >
                <div className="w-8 h-[2px] bg-primary"></div>
                <span className="text-white/60 font-mono text-xs tracking-[0.3em] uppercase">
                  Hello
                </span>
              </motion.div>

              {/* Main Heading */}
              <motion.h2
                variants={fadeRight}
                custom={1}
                initial="hidden"
                animate={isInView ? 'visible' : 'hidden'}
                className="font-heading text-4xl sm:text-5xl lg:text-6xl text-white leading-[1.1] tracking-tight mb-5 sm:mb-6"
              >
                I'M <span className="text-primary">VISHWAS</span>
              </motion.h2>

              {/* Cursive Quote */}
              <motion.p
                variants={fadeRight}
                custom={2}
                initial="hidden"
                animate={isInView ? 'visible' : 'hidden'}
                className="text-white/90 text-2xl sm:text-3xl mb-6 sm:mb-8 italic tracking-wide leading-relaxed"
                style={{ fontFamily: "'Dancing Script', 'Caveat', cursive" }}
              >
                "Racing is my excuse to stay strong"
              </motion.p>

              {/* Paragraph details */}
              <motion.div
                variants={fadeRight}
                custom={3}
                initial="hidden"
                animate={isInView ? 'visible' : 'hidden'}
                className="text-sm sm:text-base text-white/75 leading-[1.8] mb-7 sm:mb-9 max-w-xl space-y-4"
              >
                <p>
                  A post-graduate corporate professional with 18 years of experience
                  working with multinational companies. Also, a professional athlete
                  with several national awards in hockey and motorcycle racing.
                </p>
                <p className="text-white/60 font-medium">
                  Aspiring to achieve milestones in different aspects of motorsports.
                </p>
              </motion.div>

              {/* GOLD Highlight */}
              <motion.div
                variants={fadeRight}
                custom={4}
                initial="hidden"
                animate={isInView ? 'visible' : 'hidden'}
                className="mb-7 sm:mb-9 p-4 sm:p-5 rounded-xl bg-gradient-to-r from-amber-500/10 via-yellow-500/5 to-transparent border border-amber-500/20 flex items-center gap-4 sm:gap-5"
              >
                <div className="relative flex-shrink-0">
                  <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-full bg-gradient-to-br from-yellow-300 via-amber-500 to-yellow-700 flex items-center justify-center shadow-lg shadow-amber-500/20">
                    <span className="font-heading text-sm sm:text-base text-yellow-950 font-bold tracking-wider">
                      GOLD
                    </span>
                  </div>
                  <motion.div
                    animate={{ rotate: 360 }}
                    transition={{ duration: 15, repeat: Infinity, ease: 'linear' }}
                    className="absolute -inset-1.5 rounded-full border border-dashed border-amber-400/40"
                  ></motion.div>
                </div>

                <div className="flex-1 min-w-0">
                  <p className="text-[10px] sm:text-xs tracking-[0.25em] text-amber-400 font-semibold uppercase mb-1.5">
                    First Ever
                  </p>
                  <h3 className="font-heading text-sm sm:text-base md:text-lg text-white leading-snug tracking-wide">
                    KTM 390 DUKE ROAD RACE
                  </h3>
                </div>
              </motion.div>

              {/* Achievements List - FIXED SPACING */}
              <div className="space-y-3 sm:space-y-3.5">
                {achievements.map((item, i) => (
                  <motion.div
                    key={i}
                    variants={fadeRight}
                    custom={5 + i}
                    initial="hidden"
                    animate={isInView ? 'visible' : 'hidden'}
                    className="bg-white/[0.03] hover:bg-white/[0.06] border border-white/5 hover:border-primary/40 rounded-xl px-3 sm:px-4 py-3 sm:py-3.5 transition-all duration-300 group"
                  >
                    <div className="flex items-center gap-3 sm:gap-4">
                      {/* Trophy Icon */}
                      <div className="w-10 h-10 sm:w-11 sm:h-11 rounded-lg bg-primary/20 text-primary flex items-center justify-center flex-shrink-0 group-hover:bg-primary group-hover:text-white transition-all duration-300">
                        <svg
                          className="w-4 h-4 sm:w-5 sm:h-5 fill-current"
                          viewBox="0 0 24 24"
                        >
                          <path d="M19 5h-2V3H7v2H5c-1.1 0-2 .9-2 2v1c0 2.55 1.92 4.63 4.39 4.94A5.01 5.01 0 0011 15.9V18H8v2h8v-2h-3v-2.1c2.12-.39 3.79-2.03 4.39-4.34C19.08 11.23 21 9.15 21 6.6V5c0-1.1-.9-2-2-2zM5 8V7h2v3.82C5.84 10.4 5 9.3 5 8zm14 0c0 1.3-.84 2.4-2 2.82V7h2v1z" />
                        </svg>
                      </div>

                      {/* Text Content */}
                      <div className="flex-1 min-w-0">
                        <p className="font-heading text-xs sm:text-sm md:text-base text-white  tracking-wide  mb-1 truncate">
                          {item.title}
                        </p>
                        <p className="text-[10px] sm:text-xs text-white/50 tracking-wider">
                          {item.subtitle}
                        </p>
                      </div>

                      {/* Year Badge - Always Visible */}
                      <div className="flex-shrink-0">
                        <span className="inline-block font-mono text-[10px] sm:text-xs text-primary font-semibold whitespace-nowrap bg-primary/10 border border-primary/20 px-2 sm:px-2.5 py-1 sm:py-1.5 rounded-md group-hover:bg-primary group-hover:text-white group-hover:border-primary transition-all duration-300">
                          {item.year}
                        </span>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>

              {/* Personal Details Grid */}
              <motion.div
                variants={fadeRight}
                custom={9}
                initial="hidden"
                animate={isInView ? 'visible' : 'hidden'}
                className="mt-8 sm:mt-10 pt-6 sm:pt-7 border-t border-white/10 grid grid-cols-2 gap-x-4 gap-y-5"
              >
                {personalDetails.map((detail, i) => (
                  <div key={i} className="group min-w-0">
                    <span className="text-[10px] sm:text-xs tracking-[0.25em] text-white/40 uppercase block mb-2">
                      {detail.label}
                    </span>
                    <span className="font-mono text-sm sm:text-base text-white font-medium group-hover:text-primary transition-colors duration-300 block truncate">
                      {detail.value}
                    </span>
                  </div>
                ))}
              </motion.div>
            </div>

            {/* RIGHT SIDE - Image Container */}
            <motion.div
              initial={{ opacity: 0, scale: 0.98 }}
              animate={isInView ? { opacity: 1, scale: 1 } : {}}
              transition={{ duration: 0.8, delay: 0.2, ease: [0.25, 1, 0.5, 1] }}
              className="lg:col-span-5 relative min-h-[320px] sm:min-h-[400px] lg:min-h-full overflow-hidden order-1 lg:order-2 flex items-center justify-center bg-gradient-to-b from-black/20 via-transparent to-black/60"
            >
              {/* Image Element */}
              <img
                src={vishwasabt}
                alt="Vishwas Motorsport Athlete"
                className="w-full h-full object-cover object-top lg:object-center transform hover:scale-105 transition-transform duration-700 ease-out"
              />

              {/* Responsive Overlays */}
              <div className="absolute inset-0 bg-gradient-to-t from-[#0d0d0d] via-transparent to-transparent lg:hidden"></div>
              <div className="absolute inset-0 bg-gradient-to-r from-[#0d0d0d] via-transparent to-transparent hidden lg:block"></div>

              {/* Decorative dashed badge */}
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 30, repeat: Infinity, ease: 'linear' }}
                className="absolute top-5 right-5 w-12 h-12 rounded-full border border-dashed border-amber-500/40 flex items-center justify-center pointer-events-none"
              >
                <div className="w-2 h-2 rounded-full bg-amber-500"></div>
              </motion.div>

              {/* Corner accent lines */}
              <div className="absolute bottom-5 right-5 flex flex-col gap-1.5 items-end pointer-events-none">
                <div className="w-8 h-[2px] bg-primary"></div>
                <div className="w-12 h-[2px] bg-white/30"></div>
                <div className="w-5 h-[2px] bg-primary"></div>
              </div>

              {/* Vertical text label */}
              <div className="absolute left-4 bottom-8 hidden lg:block pointer-events-none">
                <span
                  className="font-mono text-[10px] tracking-[0.4em] text-white/30 uppercase"
                  style={{ writingMode: 'vertical-rl' }}
                >
                  Motorsport Athlete
                </span>
              </div>
            </motion.div>

          </div>

          {/* Bottom border accent */}
          <div className="h-0.5 bg-gradient-to-r from-transparent via-primary to-transparent"></div>
        </div>

        {/* Bottom Tag */}
        <motion.div
          variants={fadeUp}
          custom={10}
          initial="hidden"
          animate={isInView ? 'visible' : 'hidden'}
          className="mt-6 sm:mt-8 flex items-center justify-center gap-3"
        >
          <div className="w-8 sm:w-10 h-[1px] bg-white/20"></div>
          <span className="text-[10px] sm:text-xs tracking-[0.3em] text-white/40 uppercase font-mono">
            Champion • Racer • Athlete
          </span>
          <div className="w-8 sm:w-10 h-[1px] bg-white/20"></div>
        </motion.div>
      </div>
    </section>
  );
};

export default About;